import type { TicketmasterEvent } from './types';

interface CacheEntry {
  events: TicketmasterEvent[];
  timestamp: number;
  params: {
    startDateTime: string;
    endDateTime: string;
    [key: string]: any;
  };
}

class TicketmasterCache {
  private static instance: TicketmasterCache;
  private cache: Map<string, CacheEntry> = new Map();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  private constructor() {}

  public static getInstance(): TicketmasterCache {
    if (!TicketmasterCache.instance) {
      TicketmasterCache.instance = new TicketmasterCache();
    }
    return TicketmasterCache.instance;
  }

  /**
   * Generate a cache key from search parameters
   */
  private generateCacheKey(params: any): string {
    // Sort keys to ensure consistent cache keys
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {} as Record<string, any>);
    
    return JSON.stringify(sortedParams);
  }

  /**
   * Check if cache entry is valid (not expired)
   */
  private isValidCache(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < this.CACHE_DURATION;
  }

  /**
   * Get events from cache if available
   */
  public getEvents(params: any): TicketmasterEvent[] | null {
    const cacheKey = this.generateCacheKey(params);
    const cachedData = this.cache.get(cacheKey);

    if (cachedData && this.isValidCache(cachedData)) {
      return cachedData.events;
    }

    return null;
  }

  /**
   * Store events in cache
   */
  public setEvents(params: any, events: TicketmasterEvent[]): void {
    const cacheKey = this.generateCacheKey(params);
    
    this.cache.set(cacheKey, {
      events,
      timestamp: Date.now(),
      params
    });
  }

  /**
   * Clear all cache entries
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear cache entries for specific date range
   */
  public clearCacheForDateRange(startDateTime: string, endDateTime: string): void {
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (
        entry.params.startDateTime === startDateTime &&
        entry.params.endDateTime === endDateTime
      ) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): {
    size: number;
    oldestEntry: number | null;
    newestEntry: number | null;
  } {
    let oldestTimestamp: number | null = null;
    let newestTimestamp: number | null = null;

    this.cache.forEach(entry => {
      if (oldestTimestamp === null || entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
      }
      if (newestTimestamp === null || entry.timestamp > newestTimestamp) {
        newestTimestamp = entry.timestamp;
      }
    });

    return {
      size: this.cache.size,
      oldestEntry: oldestTimestamp,
      newestEntry: newestTimestamp
    };
  }
}

export const ticketmasterCache = TicketmasterCache.getInstance();